import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'

const ROW_OPTIONS = [10, 20, 30]

function CustomPagination ({
  currentPage = 1,
  rowsPerPage = 10,
  totalRecords = 0,
  onPageChange,
  totalPages,
  rounded = false,
  className = '',
  mirrorFooter = true
}) {
  const { t } = useTranslation()
  const containerRef = useRef(null)
  const [footerTarget, setFooterTarget] = useState(null)
  const safeRowsPerPage = Number(rowsPerPage) || 10
  const safeTotalRecords = Number(totalRecords) || 0
  const computedTotalPages = Math.max(
    1,
    Number(totalPages) || Math.ceil(safeTotalRecords / safeRowsPerPage) || 1
  )
  const safeCurrentPage = Math.min(
    Math.max(Number(currentPage) || 1, 1),
    computedTotalPages
  )
  const firstItem =
    safeTotalRecords === 0 ? 0 : (safeCurrentPage - 1) * safeRowsPerPage + 1
  const lastItem = Math.min(safeCurrentPage * safeRowsPerPage, safeTotalRecords)

  const handleRowsChange = event => {
    onPageChange?.(1, Number(event.target.value))
  }

  const goToPage = page => {
    if (page < 1 || page > computedTotalPages || page === safeCurrentPage) {
      return
    }
    onPageChange?.(page, safeRowsPerPage)
  }

  const handlePageChange = event => {
    const nextPage = Number(event.target.value)
    if (!nextPage) return
    goToPage(Math.min(Math.max(nextPage, 1), computedTotalPages))
  }

  useEffect(() => {
    if (!mirrorFooter || typeof document === 'undefined') {
      setFooterTarget(null)
      return undefined
    }

    const container = containerRef.current
    const parent = container?.parentElement
    if (!container || !parent) {
      setFooterTarget(null)
      return undefined
    }

    const table = Array.from(parent.children).find(
      child =>
        child !== container &&
        (child.classList?.contains('custom-table') ||
          child.querySelector?.('.custom-table'))
    )

    if (!table) {
      setFooterTarget(null)
      return undefined
    }

    const footer = document.createElement('div')
    footer.setAttribute('data-custom-pagination-footer', 'true')
    table.insertAdjacentElement('afterend', footer)
    setFooterTarget(footer)

    return () => {
      setFooterTarget(null)
      footer.remove()
    }
  }, [mirrorFooter])

  const paginationContent = ({ isFooter = false } = {}) => (
    <div
      ref={isFooter ? undefined : containerRef}
      className={`custom-pagination flex h-[38px] w-full items-stretch overflow-hidden border border-gray-200 bg-[#f5f5f5] text-sm text-gray-900 ${
        rounded
          ? 'rounded-md'
          : isFooter
            ? 'rounded-b-md border-t-0'
            : 'rounded-t-md border-b-0'
      } ${className}`}
    >
      <div className='flex h-full items-center gap-3 border-r border-gray-200 px-4'>
        <span className='whitespace-nowrap'>{t('Items per page')}:</span>
        <select
          value={safeRowsPerPage}
          onChange={handleRowsChange}
          className='h-8 min-w-[56px] border-0 bg-transparent px-1 text-sm outline-none focus:ring-0'
          aria-label={t('Items per page')}
        >
          {ROW_OPTIONS.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className='flex h-full flex-1 items-center border-r border-gray-200 px-4 text-gray-700'>
        {firstItem}-{lastItem} {t('of')} {safeTotalRecords} {t('items')}
      </div>

      <div className='flex h-full items-center gap-3 border-r border-gray-200 px-4'>
        <select
          value={safeCurrentPage}
          onChange={handlePageChange}
          className='h-8 w-[64px] border-0 bg-transparent px-1 text-sm leading-8 outline-none focus:ring-0'
          aria-label={t('Page')}
        >
          {Array.from({ length: computedTotalPages }, (_, index) => index + 1).map(
            page => (
              <option key={page} value={page}>
                {page}
              </option>
            )
          )}
        </select>
        <span className='whitespace-nowrap'>
          {t('of')} {computedTotalPages} {t('pages')}
        </span>
      </div>

      <button
        type='button'
        onClick={() => goToPage(safeCurrentPage - 1)}
        disabled={safeCurrentPage <= 1}
        className='flex h-full w-10 items-center justify-center border-r border-gray-200 bg-[#eeeeee] text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:bg-[#f5f5f5] disabled:text-gray-300'
        aria-label={t('Previous')}
      >
        <FaChevronLeft className='text-[11px]' />
      </button>
      <button
        type='button'
        onClick={() => goToPage(safeCurrentPage + 1)}
        disabled={safeCurrentPage >= computedTotalPages}
        className='flex h-full w-10 items-center justify-center bg-[#eeeeee] text-gray-900 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:bg-[#f5f5f5] disabled:text-gray-300'
        aria-label={t('Next')}
      >
        <FaChevronRight className='text-[11px]' />
      </button>
    </div>
  )

  return (
    <>
      {paginationContent()}
      {footerTarget &&
        createPortal(paginationContent({ isFooter: true }), footerTarget)}
    </>
  )
}

export default CustomPagination
